import { debounce } from 'lodash-es';
import { createElement, FC, MouseEvent, SyntheticEvent } from 'react';

import {
  DynamicSchemaDefinitionField,
  DynamicSchemaDefinitionFiles,
  DynamicSchemaDefinitionFixed,
  DynamicSchemaDefinitionMultiLineText,
  DynamicSchemaDefinitionPositiveInteger,
  DynamicSchemaDefinitionSelectMultiple,
  DynamicSchemaDefinitionSelectOne,
  DynamicSchemaDefinitionSingleLineText,
  DynamicSchemaDefinitionToggle,
} from '../../../../../generated/graphql';
import {
  AnyFieldValue,
  FilesValue,
  MultiLineTextValue,
  PositiveIntegerValue,
  SelectMultipleValue,
  SelectOneValue,
  SingleLineTextValue,
  ToggleValue,
} from '../../../../types/DynamicSchema';
import { FileReference, UploadingFile, UploadingFilesProgress } from '../../../../types/Files';
import FilesField from './FilesField';
import FixedField from './FixedField';
import MultiLineTextField from './MultiLineTextField';
import PositiveIntegerField from './PositiveIntegerField';
import SelectMultipleField from './SelectMultipleField';
import SelectOneField from './SelectOneField';
import SingleLineTextField from './SingleLineTextField';
import ToggleField from './ToggleField';

type Props = {
  field: DynamicSchemaDefinitionField;
  value?: AnyFieldValue;
  files?: ReadonlyArray<FileReference>;
  uploadingFiles?: ReadonlyArray<UploadingFile>;
  uploadingFilesProgress: UploadingFilesProgress;
  errors: Array<Error>;
  onChange: (e: SyntheticEvent<HTMLElement>, value: AnyFieldValue) => void;
  onRequestDeleteFile: (e: MouseEvent<HTMLElement>, file: FileReference, bucketId: string) => void;
  onRequestUploadFiles: (e: SyntheticEvent<HTMLElement>, files: ReadonlyArray<UploadingFile>, bucketId: string) => void;
  className?: string;
};

const Field: FC<Props> = ({
  field,
  value,
  files,
  uploadingFiles,
  uploadingFilesProgress,
  errors,
  onChange,
  onRequestDeleteFile,
  onRequestUploadFiles,
  className,
}) => {
  // Type predicateを使った判別方法を行えばここまでtype assertionは必要ないが、type predicateを使うとボイラープレートがかなり増え、
  // かと言って可読性も増すわけではないので、type assertionを使った実装を行っている。
  switch (field.type) {
    case 'SELECT_ONE':
      return (
        <SelectOneField
          field={field as DynamicSchemaDefinitionSelectOne}
          value={value as SelectOneValue}
          onChange={onChange}
          className={className}
        />
      );
    case 'SELECT_MULTIPLE':
      return (
        <SelectMultipleField
          field={field as DynamicSchemaDefinitionSelectMultiple}
          value={(value as SelectMultipleValue) || []}
          errors={errors}
          onChange={onChange}
          className={className}
        />
      );
    case 'TOGGLE':
      return (
        <ToggleField
          field={field as DynamicSchemaDefinitionToggle}
          value={value as ToggleValue}
          onChange={onChange}
          className={className}
        />
      );
    case 'SINGLE_LINE_TEXT':
      return (
        <SingleLineTextField
          field={field as DynamicSchemaDefinitionSingleLineText}
          value={value as SingleLineTextValue}
          errors={errors}
          onChange={debounce(onChange, 250)}
          className={className}
        />
      );
    case 'MULTI_LINE_TEXT':
      return (
        <MultiLineTextField
          field={field as DynamicSchemaDefinitionMultiLineText}
          value={value as MultiLineTextValue}
          errors={errors}
          onChange={debounce(onChange, 250)}
          className={className}
        />
      );
    case 'POSITIVE_INTEGER':
      return (
        <PositiveIntegerField
          field={field as DynamicSchemaDefinitionPositiveInteger}
          value={value as PositiveIntegerValue}
          errors={errors}
          onChange={debounce(onChange, 250)}
          className={className}
        />
      );
    case 'FILES':
      // `field.type === DynamicSchemaDefinitionFiles`の場合、必ずfilesやuploadingFilesが渡されると推定するので、
      // `files={files!}`と`uploadingFiles={uploadingFiles!}`と、non null assertionを入れている。
      return (
        <FilesField
          field={field as DynamicSchemaDefinitionFiles}
          bucketId={value as FilesValue}
          files={files!}
          uploadingFiles={uploadingFiles!}
          uploadingFilesProgress={uploadingFilesProgress}
          onRequestDeleteFile={onRequestDeleteFile}
          onRequestUploadFiles={onRequestUploadFiles}
          className={className}
        />
      );
    case 'FIXED':
      return <FixedField field={field as DynamicSchemaDefinitionFixed} className={className} />;
    default:
      return null;
  }
};

export default Field;
